/**
 * Role-gated UI. The server decides everything; the UI only hides what a user
 * cannot use (from GET /api/v2/me). Never check role names — check
 * capabilities, admin areas or the explicit flags.
 */

import type { Me } from "@/api/contracts";

export interface Requirement {
  /** A capability the user must hold on at least one target, e.g. "knowledge.write". */
  capability?: string;
  /** Any one of these capabilities. */
  anyCapability?: string[];
  /** An admin UI area (admin.<area>). */
  adminArea?: string;
  /** Any admin area at all. */
  anyAdmin?: boolean;
  audit?: boolean;
  approve?: boolean;
  breakGlass?: boolean;
}

export function hasCapability(me: Me | null | undefined, capability: string): boolean {
  if (!me) return false;
  if (me.superadmin) return true;
  if (capability.startsWith("self.")) return true;
  if (capability.startsWith("admin.") && me.adminAreas.includes(capability.slice(6))) return true;
  return me.capabilities.includes(capability);
}

export function allowed(me: Me | null | undefined, req: Requirement | undefined): boolean {
  if (!req) return true;
  if (!me) return false;
  if (req.capability && !hasCapability(me, req.capability)) return false;
  if (req.anyCapability && !req.anyCapability.some((c) => hasCapability(me, c))) return false;
  if (req.adminArea && !(me.superadmin || me.adminAreas.includes(req.adminArea))) return false;
  if (req.anyAdmin && !(me.superadmin || me.adminAreas.length > 0 || hasCapability(me, "knowledge.admin") || hasCapability(me, "feedback.admin"))) return false;
  if (req.audit && !me.canReadAllAudit) return false;
  if (req.approve && !me.canApprove) return false;
  if (req.breakGlass && !me.canBreakGlass) return false;
  return true;
}
